import emailjs from '@emailjs/browser';

// Configuration EmailJS
// Vous devrez créer un compte sur https://www.emailjs.com/ et remplacer ces valeurs
const EMAILJS_CONFIG = {
  SERVICE_ID: 'service_xxxxxxx', // À remplacer avec votre Service ID
  TEMPLATE_ID: 'template_xxxxxxx', // À remplacer avec votre Template ID
  PUBLIC_KEY: 'xxxxxxxxxxxxxxx', // À remplacer avec votre Public Key
  ENABLED: false // 🔴 Désactiver les emails automatiques
};

export interface DeadlineNotification {
  entreprise: string;
  client: string;
  type: string;
  echeance: string;
  daysUntil: number;
  urgency: 'overdue' | 'urgent' | 'warning';
}

export class EmailNotificationService {
  private static initialized = false;

  static init() {
    if (!this.initialized) {
      emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
      this.initialized = true;
    }
  }

  static async sendDeadlineAlert(notification: DeadlineNotification): Promise<boolean> {
    // 🔴 Vérifier si les emails sont activés
    if (!EMAILJS_CONFIG.ENABLED) {
      console.log('📧 Envoi d\'email désactivé - Notification non envoyée:', notification);
      return true; // Retourner true pour ne pas bloquer le flux
    }

    this.init();

    try {
      // Préparer les données pour le template email
      const templateParams = {
        to_email: 'pjleroy@webapic.com',
        subject: this.getSubject(notification),
        entreprise: notification.entreprise,
        client: notification.client,
        type: notification.type,
        echeance: notification.echeance,
        days_until: notification.daysUntil,
        urgency_text: this.getUrgencyText(notification.urgency),
        message: this.getMessage(notification)
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams
      );

      console.log('Email envoyé avec succès:', response);
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      return false;
    }
  }

  private static getSubject(notification: DeadlineNotification): string {
    switch (notification.urgency) {
      case 'overdue':
        return `🚨 URGENT - Échéance dépassée: ${notification.entreprise}`;
      case 'urgent':
        return `⚠️ URGENT - Échéance dans ${notification.daysUntil} jour(s): ${notification.entreprise}`;
      case 'warning':
        return `📅 Attention - Échéance dans ${notification.daysUntil} jours: ${notification.entreprise}`;
      default:
        return `📋 Rappel d'échéance: ${notification.entreprise}`;
    }
  }

  private static getUrgencyText(urgency: string): string {
    switch (urgency) {
      case 'overdue':
        return 'EN RETARD';
      case 'urgent':
        return 'URGENT';
      case 'warning':
        return 'ATTENTION';
      default:
        return 'RAPPEL';
    }
  }

  private static getMessage(notification: DeadlineNotification): string {
    let message = `Bonjour,\n\n`;
    
    if (notification.urgency === 'overdue') {
      const daysPast = Math.abs(notification.daysUntil);
      message += `⚠️ Une échéance est dépassée depuis ${daysPast} jour${daysPast > 1 ? 's' : ''} !\n\n`;
    } else {
      message += `📅 Une échéance approche !\n\n`;
    }

    message += `Détails de la formalité :\n`;
    message += `• Entreprise : ${notification.entreprise}\n`;
    message += `• Client : ${notification.client}\n`;
    message += `• Type : ${notification.type}\n`;
    message += `• Échéance : ${notification.echeance}\n`;
    
    if (notification.urgency === 'overdue') {
      message += `• Statut : EN RETARD de ${Math.abs(notification.daysUntil)} jour${Math.abs(notification.daysUntil) > 1 ? 's' : ''}\n\n`;
      message += `⚡ Action requise immédiatement !\n\n`;
    } else {
      message += `• Délai restant : ${notification.daysUntil} jour${notification.daysUntil > 1 ? 's' : ''}\n\n`;
      message += `🔔 Merci de prendre les mesures nécessaires.\n\n`;
    }

    message += `Cordialement,\n`;
    message += `Système de gestion des formalités`;

    return message;
  }

  // Envoyer un rapport quotidien des échéances
  static async sendDailyReport(notifications: DeadlineNotification[]): Promise<boolean> {
    // 🔴 Vérifier si les emails sont activés
    if (!EMAILJS_CONFIG.ENABLED) {
      console.log('📧 Envoi d\'email désactivé - Rapport non envoyé');
      return true;
    }

    this.init();

    if (notifications.length === 0) return true;

    try {
      const overdue = notifications.filter(n => n.urgency === 'overdue');
      const urgent = notifications.filter(n => n.urgency === 'urgent');
      const warning = notifications.filter(n => n.urgency === 'warning');

      let message = `Rapport quotidien des échéances\n`;
      message += `================================\n\n`;

      if (overdue.length > 0) {
        message += `🚨 ÉCHÉANCES DÉPASSÉES (${overdue.length}) :\n`;
        overdue.forEach(n => {
          message += `• ${n.entreprise} - ${n.type} (${Math.abs(n.daysUntil)} jour${Math.abs(n.daysUntil) > 1 ? 's' : ''} de retard)\n`;
        });
        message += `\n`;
      }

      if (urgent.length > 0) {
        message += `⚠️ ÉCHÉANCES URGENTES (≤3 jours) (${urgent.length}) :\n`;
        urgent.forEach(n => {
          message += `• ${n.entreprise} - ${n.type} (${n.daysUntil} jour${n.daysUntil > 1 ? 's' : ''})\n`;
        });
        message += `\n`;
      }

      if (warning.length > 0) {
        message += `📅 ÉCHÉANCES À SURVEILLER (≤7 jours) (${warning.length}) :\n`;
        warning.forEach(n => {
          message += `• ${n.entreprise} - ${n.type} (${n.daysUntil} jour${n.daysUntil > 1 ? 's' : ''})\n`;
        });
        message += `\n`;
      }

      const templateParams = {
        to_email: 'pjleroy@webapic.com',
        subject: `📊 Rapport quotidien - ${overdue.length + urgent.length + warning.length} échéance(s) à traiter`,
        message: message
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams
      );

      console.log('Rapport quotidien envoyé:', response);
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'envoi du rapport:', error);
      return false;
    }
  }
}

// Service local de stockage pour éviter les doublons
export class NotificationStorageService {
  private static STORAGE_KEY = 'deadline_notifications_sent';

  static getSentNotifications(): string[] {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static addSentNotification(notificationId: string): void {
    const sent = this.getSentNotifications();
    if (!sent.includes(notificationId)) {
      sent.push(notificationId);
      // Garder seulement les 1000 derniers pour éviter l'encombrement
      if (sent.length > 1000) {
        sent.splice(0, sent.length - 1000);
      }
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sent));
    }
  }

  static wasNotificationSent(notificationId: string): boolean {
    return this.getSentNotifications().includes(notificationId);
  }

  static clearOldNotifications(): void {
    // Nettoyer les notifications de plus de 30 jours
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Générer un ID unique pour une notification
  static generateNotificationId(entreprise: string, echeance: string, urgency: string): string {
    const today = new Date().toISOString().split('T')[0];
    return `${entreprise}-${echeance}-${urgency}-${today}`;
  }
}
